import { ReactNode, useState } from 'react'
import Asset from 'api/types/models/asset'
import { FormatDateTime } from 'components/format-datetime'
import Input from 'components/form/input'
import Button from 'components/button'

type Props = {
  asset?: Asset | null
}

type RowProps = {
  label: string
  children: ReactNode
}

const Row = ({ label, children }: RowProps) => (
  <div className="flex items-baseline justify-between gap-3 py-2 border-b border-secondary-gray">
    <dt className="text-xs font-medium tracking-wide text-gray-500 uppercase">{label}</dt>
    <dd className="text-sm text-right text-gray-800 break-words">{children}</dd>
  </div>
)

const UrlParam = ({ value }: { value?: string }) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    if (!value) return

    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard unavailable (e.g. insecure context) — leave the field for manual copy.
    }
  }

  return (
    <div className="pt-3">
      <dt className="mb-1 text-xs font-medium tracking-wide text-gray-500 uppercase">URL param</dt>
      <div className="flex gap-2">
        <Input value={value} disabled />
        <Button
          variant="secondary"
          onClick={handleCopy}
          isSuccess={copied}
          successText="Copied"
        >
          Copy
        </Button>
      </div>
    </div>
  )
}

const AssetInfoCard = ({ asset }: Props) => {
  return (
    <div className="p-4 border rounded-md bg-gray-50 border-secondary-gray">
      <h3 className="mb-2 text-sm font-medium text-gray-900">Asset info</h3>
      <dl>
        <Row label="Serial">{asset?.serial || 'Not Set'}</Row>
        <Row label="Product">{asset?.product?.name}</Row>
        <Row label="Domain">
          <a href={`https://${asset?.domain}`} className="url-text" target="__blank">
            {asset?.domain}
          </a>
        </Row>
        <Row label="Spec">Generation {asset?.spec}</Row>
        <Row label="Mfg year">
          <FormatDateTime value={asset?.manufactured_at} format="yyyy" />
        </Row>
        <UrlParam value={asset?.masked_uid} />
      </dl>
    </div>
  )
}

export default AssetInfoCard
